import { useBoundStore } from 'store';
import { Maybe, MaybeNull } from 'types';
import { Dictionary } from 'contexts/LocalizationContext/types';
import { RenvoiMode } from 'clients/documents/documentsClient.types';

const inReviewMode = () => !!useBoundStore.getState().reviewMode;

export const getRenvoiModeLabel = (mode: Maybe<RenvoiMode>, dictionary: Dictionary) => {
  const map = {
    [RenvoiMode.Normal]: dictionary.renvoiMode.normal,
    [RenvoiMode.Strikethrough]: dictionary.renvoiMode.strikethrough,
    [RenvoiMode.Removed]: dictionary.renvoiMode.removed,
  };

  return map[mode || RenvoiMode.Normal];
};

export const getParentPrintCheckboxes = (itemSlug: string) => {
  const element = window.document.querySelector(`.print-dialog ${itemSlug ? `#${itemSlug}` : ''}`);

  if (!element || !itemSlug) {
    return [];
  }

  const parentCheckboxes: HTMLInputElement[] = [];
  let parentElement = element.closest(`.print-dialog .document-child-item:not(#${itemSlug})`);

  while (parentElement) {
    const checkbox = parentElement.querySelector('.print-selection-checkbox > input');
    const parentElementId = (parentElement.getAttribute('id') || '').replaceAll('.', '\\.');

    if (checkbox) {
      parentCheckboxes.push(checkbox as HTMLInputElement);
    }

    if (!parentElementId) {
      break;
    }

    parentElement = parentElement.closest(`.print-dialog .document-child-item:not(#${parentElementId})`);
  }

  return parentCheckboxes;
};

export const getChildPrintCheckboxes = (itemSlug?: Maybe<string>) => {
  const parentItem = window.document.querySelector(`.print-dialog ${itemSlug ? `#${itemSlug}` : ''}`);
  return parentItem?.querySelectorAll('.print-selection-checkbox > input') || [];
};

export const checkAllRelatedPrintCheckboxes = (itemSlug: string, shouldPrint: boolean = false) => {
  const escapedSlug = itemSlug.replaceAll('.', '\\.');
  const childCheckBoxes = getChildPrintCheckboxes(escapedSlug);
  const parentCheckboxes = shouldPrint && itemSlug ? getParentPrintCheckboxes(escapedSlug) : [];

  const relatedCheckboxes = [
    ...childCheckBoxes,
    ...parentCheckboxes,
  ];

  const slugsToUpdate: Maybe<MaybeNull<string>>[] = [];

  (relatedCheckboxes as HTMLInputElement[]).forEach((checkbox) => {
    const childItemSlug = checkbox.parentElement?.parentElement?.getAttribute('data-slug');
    slugsToUpdate.push(childItemSlug);
    checkbox.checked = shouldPrint;
  });

  return slugsToUpdate.reduce((accumulator, slug) => {
    return slug ? {
      ...accumulator,
      [slug]: shouldPrint,
    } : accumulator;
  }, {});
};

type EntityType = 'block' | 'article' | 'section';
type FieldType = 'title' | 'text_content' | 'json_content';

export const getAddOrDeleteEntityColorForHistory = (
  entityId: number,
  entityType: EntityType,
): string | undefined => {
  if (!inReviewMode()) return;

  const historyData = useBoundStore.getState().historyData;
  if (!historyData) return;

  const newKey = `new_${entityType}s` as const;
  const delKey = `deleted_${entityType}s` as const;

  const inNew =
			Object.values(historyData[newKey]).some(arr => arr.includes(entityId));
  if (inNew) return 'rgba(76, 175, 80, 0.2)';

  const inDeleted =
			Object.values(historyData[delKey]).some(arr => arr.includes(entityId));
  if (inDeleted) return 'rgba(244, 67, 54, 0.2)';

  return;
};

export const getModifiedEntityColorForHistory = (
  entityId: number,
  entityType: EntityType,
  field: FieldType,
): string | undefined => {
  if (!inReviewMode()) return;
  
  const historyData = useBoundStore.getState().historyData;
  if (!historyData) return;

  const modKey = `modified_${entityType}s` as const;
  const arr = historyData[modKey][field];

  if (arr?.includes(entityId)) {
    return field === 'title'
      ? 'rgba(255, 193, 7, 0.3)'
      : 'rgba(255, 193, 7, 0.3)';
  }
  return;
};

export const hasEntityChanged = (entityId: number, entityType: EntityType): boolean => {
  if (!inReviewMode()) return false;

  const historyData = useBoundStore.getState().historyData;
  if (!historyData) return false;

  const newKey = `new_${entityType}s` as const;
  const delKey = `deleted_${entityType}s` as const;
  const modKey = `modified_${entityType}s` as const;

  if (Object.values(historyData[newKey]).some(arr => arr.includes(entityId))) return true;
  if (Object.values(historyData[delKey]).some(arr => arr.includes(entityId))) return true;
  if (Object.values(historyData[modKey]).some(arr => arr.includes(entityId))) return true;

  return false;
};

export const getFieldChangeColor = (
  entityId: number,
  entityType: EntityType,
  field: FieldType,
): string | undefined => {
  if (!inReviewMode()) return;

  const h = useBoundStore.getState().historyData;
  if (!h) return;

  const newKey = `new_${entityType}s` as const;
  const delKey = `deleted_${entityType}s` as const;
  const modKey = `modified_${entityType}s` as const;

  const added = h[newKey]?.[field]?.includes(entityId);
  if (added) return 'rgba(76, 175, 80, 0.2)';

  const deleted = h[delKey]?.[field]?.includes(entityId);
  if (deleted) return 'rgba(244, 67, 54, 0.2)';

  const modified = h[modKey]?.[field]?.includes(entityId);
  if (modified) return 'rgba(255, 193, 7, 0.3)';

  return;
};